import React, { useMemo, useState, useId } from "react";

export default function YouTubePlayer({
  url,
  title,
}: {
  url: string;
  title?: string;
}) {
  const [playing, setPlaying] = useState(false);
  const vid = useMemo(() => extractYouTubeId(url), [url]);
  const thumb = useMemo(
    () => (vid ? `https://i.ytimg.com/vi/${vid}/hqdefault.jpg` : undefined),
    [vid]
  );
  const iframeTitle = title || "YouTube video";
  const linkId = useId();

  if (!vid) {
    return (
      <a
        className="text-blue-600 underline break-all"
        href={url}
        target="_blank"
        rel="noopener noreferrer"
      >
        {url}
      </a>
    );
  }

  return (
    <div className="w-full max-w-2xl">
      {/* Top action bar */}
      <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
        <a
          id={linkId}
          className="underline hover:text-gray-800"
          href={`https://www.youtube.com/watch?v=${vid}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          Open on YouTube ↗
        </a>
      </div>

      {/* Player area with 16:9 aspect */}
      <div className="relative rounded-2xl overflow-hidden shadow-sm border border-gray-200">
        <div className="aspect-video bg-black">
          {playing ? (
            <iframe
              className="w-full h-full"
              src={`https://www.youtube-nocookie.com/embed/${vid}?autoplay=1&modestbranding=1&rel=0`}
              title={iframeTitle}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
            />
          ) : (
            <button
              type="button"
              aria-label="Play video"
              onClick={() => setPlaying(true)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setPlaying(true);
                }
              }}
              className="group relative w-full h-full focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
            >
              {thumb ? (
                <img
                  src={thumb}
                  alt={title || "YouTube thumbnail"}
                  loading="lazy"
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full bg-gray-900" />
              )}

              {/* Dark gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/20 to-transparent" />

              {/* Centered play button */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="rounded-full p-4 sm:p-5 bg-white/90 group-hover:bg-white shadow">
                  <svg
                    width="28"
                    height="28"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path d="M8 5v14l11-7z" />
                  </svg>
                </div>
              </div>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/** Extracts a YouTube video id from common URL formats. */
export function extractYouTubeId(input: string): string | null {
  try {
    const url = new URL(input);
    if (/(youtube\.com|youtu\.be)/i.test(url.hostname)) {
      if (url.hostname.includes("youtu.be")) {
        const id = url.pathname.slice(1);
        return id ? cleanId(id) : null;
      }
      if (url.searchParams.has("v")) {
        return cleanId(url.searchParams.get("v") || "");
      }
      const parts = url.pathname.split("/").filter(Boolean);
      const embedIndex = parts.indexOf("embed");
      if (embedIndex >= 0 && parts[embedIndex + 1]) {
        return cleanId(parts[embedIndex + 1]);
      }
    }
    return null;
  } catch {
    return null;
  }
}

function cleanId(id: string): string {
  return id.replace(/[^a-zA-Z0-9_-].*$/, "");
}
